import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { onlyNumbers } from '../string';
import codes from './phoneMasks';
/**
 * Выполняет проверку на соответствие номера телефона кодам регионов в
 * переданном объекте кода страны
 * @param {string} phone Номер телефона, для которого необходимо произвести
 * проверку на соответствие кодам регионов
 * @param {Object} mask Объект с данными кода страны, для которого необходимо
 * произвести проверку
 * @returns {boolean} Указывает - соответствует ли номер телефона кодам
 * регионов в переданном объекте кода страны
 */

var checkAreaCode = function checkAreaCode(phone, mask) {
  return mask.areaCodes.some(function (areaCode) {
    return areaCode === phone.slice(mask.count, mask.count + areaCode.length);
  });
};
/**
 * Создаёт функция для сравнения конфигураций кодов стран для переданного
 * номера телефона и аббревиатуры страны. Сравнение происходит по соответствии
 * переданного номера полному регулярному выражению в ключе `valid` объекта
 * кода, далее по сопоставлению переданной в аргументах страны со страной кода
 * или же по длине кода
 * @param {string} phone Номер телефона, для которого необходимо произвести
 * сравнение подходящих кодов стран
 * @param {string} [country] Аббревиатура предположительной страны, которой
 * соответствует номер телефона
 * @returns {(a: Object, b: Object) => boolean} Функция для сравнения двух
 * кодов, возвращает true, если первый код приоритетнее второго, иначе false
 */


var compareCodes = function compareCodes(phone, country) {
  return function (a, b) {
    return a.valid.test(phone) && !b.valid.test(phone) || country && a.country === country && b.country !== country || a.count > b.count;
  };
};
/**
 * Выполняет поиск кода страны по переданному в первом аргументе номеру
 * телефона (возможно неполному). Поиск ведётся в массиве из файла
 * src/utils/phone/phoneMasks.js. С помощью второго аргумента можно указать
 * аббревиатуру страны, код которой, вероятнее всего, необходимо найти.
 * @param {string} phone Номер телефона (возможно неполный), для которого
 * необходимо найти код страны
 * @param {string} [country] Аббревиатура предположительной страны, поиск кода
 * которой ведётся
 * @returns {Object} Код номера телефона страны, который наиболее соответствует
 * переданному номеру телефона
 */


var findCodeByNumber = function findCodeByNumber(phone, country) {
  // Поиск подходящих кодов стран к переданному номеру и помещение их в массив
  // phoneMasks
  var masks = codes.filter(function (code) {
    if (phone.search(code.test) === 0) {
      return code.areaCodes ? checkAreaCode(phone, code) : true;
    }

    return false;
  }); // Если найден только один код, то нет необходимости в дальнейших действиях и
  // следует вернуть этот код

  if (masks.length === 1) {
    return masks[0];
  } // Создание функции для приоритизации найденных кодов стран


  var compare = compareCodes(phone, country); // Сортировка списка найденных кодов стран по их приоритету, приоритет
  // определяется по соответствии переданного номера полному регулярному
  // выражению в ключе `valid` объекта кода, по сопоставлению переданной в
  // аргументах страны со страной кода и по длине кода

  masks = masks.sort(function (a, b) {
    if (compare(a, b)) {
      return -1;
    }

    if (compare(b, a)) {
      return 1;
    }

    return 0;
  });
  return masks[0];
};
/**
 * Выполняет поиск кода страны по переданному номеру телефона и Аббревиатура
 * страны
 * @param {string} phone Номер телефона (возможно неполный), для которого
 * необходимо найти код страны
 * @param {string} [country] Аббревиатура предположительной страны, поиск кода
 * которой ведётся
 * @returns {Array} Массив, где первый элемент - это найденный код страны, а
 * второй - это обработанный номер телефона, в котором удалены все символы,
 * кроме цифр
 */


var findCode = function findCode(phone, country) {
  var number = onlyNumbers(phone);
  var code = findCodeByNumber(number, country);
  return [code, number];
};
/**
 * Выполняет обработку номера телефона, ища код страны, которая подходит под
 * переданный в аргументах номер и подставляя в её маску цифры из этого номера
 * @param {string} value Номер телефона, который необходимо обработать
 * @param {string} [country] Аббревиатура предположительной страны, поиск кода
 * которой ведётся
 * @returns {string} Обработанный номер телефона
 */


export var normalizePhoneByCountry = function normalizePhoneByCountry(value, country) {
  if (!value) {
    return value;
  }

  var _findCode = findCode(value, country),
      _findCode2 = _slicedToArray(_findCode, 2),
      code = _findCode2[0],
      phone = _findCode2[1];

  if (!code) {
    return value // eslint-disable-next-line no-useless-escape
    .replace(/[^\d\+\-\(\)]/g, '').replace(/^\+{0,1}7\({0,1}0/g, '+7(');
  }

  var i = 0;
  var normalizedPhone = code.mask // eslint-disable-next-line no-plusplus, no-unused-vars
  .replace(/(#)/gi, function () {
    return phone[code.count + i++] || '';
  });
  return value.endsWith(')') ? normalizedPhone.replace(/(.*\d)(\))?.*/, '$1$2') : normalizedPhone.replace(/(.*\d).*/, '$1');
};
export var normalizePhone = normalizePhoneByCountry;
/**
 * Выполняет поиск страны, которой соответствует переданный номер телефона
 * @param {string} value Номер телефона, для которого необходимо определить
 * страну
 * @param {boolean} [withDefault] Флаг, который указывает - необходимо ли
 * вернуть страну по умолчанию при отсутствии номера телефона
 * @param {string} [country] Аббревиатура предположительной страны, поиск кода
 * которой ведётся
 * @returns {string} Аббревиатура страны, которой соответствует переданный номер
 * телефона
 */

export var getPhoneCountry = function getPhoneCountry(value, withDefault, country) {
  if (!value && withDefault) {
    return 'RU';
  }

  var _findCode3 = findCode(value, country),
      _findCode4 = _slicedToArray(_findCode3, 1),
      code = _findCode4[0];

  if (!code) {
    if (withDefault) {
      return 'RU';
    }

    return undefined;
  }

  return code.country;
};
export var getCountryCodes = codes.reduce(function (acc, _ref) {
  var country = _ref.country,
      code = _ref.code;

  if (Array.isArray(country)) {
    country.forEach(function (item) {
      acc[item] = code;
    });
  } else {
    acc[country] = code;
  }

  return acc;
}, {});